// src/pages/CollectionCreatePage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createCollection, getCollections } from "../helpers/Collections";
import { getAttributes, getAttributeValues } from "../helpers/Attributes";

type Attr = { id: number; name: string };
type AttrValue = { id: number; value: string };
type FaqItem = { question: string; answer: string };
type MiniCollection = { id: number; name: string };

export default function CollectionCreate() {
    const navigate = useNavigate();

    const [saving, setSaving] = useState(false);

    // form
    const [name, setName] = useState("");
    const [shortDescription, setShortDescription] = useState("");
    const shortMax = 255;

    // filters (attribute -> values)
    const [attributes, setAttributes] = useState<Attr[]>([]);
    const [attrId, setAttrId] = useState<number | "">("");
    const [attrValues, setAttrValues] = useState<AttrValue[]>([]);
    const [attrValuesSearch, setAttrValuesSearch] = useState("");
    const [selectedValueIds, setSelectedValueIds] = useState<number[]>([]);

    // similar collections
    const [similarSearch, setSimilarSearch] = useState("");
    const [similarResults, setSimilarResults] = useState<MiniCollection[]>([]);
    const [similarSelected, setSimilarSelected] = useState<MiniCollection[]>([]);

    // faq
    const [faq, setFaq] = useState<FaqItem[]>([
        { question: "", answer: "" }
    ]);

    // abort refs
    const attrValuesAbort = useRef<AbortController | null>(null);
    const attrsAbort = useRef<AbortController | null>(null);
    const similarAbort = useRef<AbortController | null>(null);

    // ------ load attributes list
    useEffect(() => {
        (async () => {
            try {
                if (attrsAbort.current) attrsAbort.current.abort();
                attrsAbort.current = new AbortController();
                const res = await getAttributes(500, 1, null, { signal: attrsAbort.current.signal });
                // API может вернуть {data: []} или просто []
                const list: Attr[] = Array.isArray(res) ? res : (res?.data ?? []);
                setAttributes(list);
            } catch (e: any) {
                if (e?.name === "AbortError") {
                    // ignore
                } else {
                    console.error(e);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------ load attribute values for chosen attribute + search
    const loadAttrValues = useCallback(async (id: number, q: string) => {
        try {
            if (attrValuesAbort.current) attrValuesAbort.current.abort();
            attrValuesAbort.current = new AbortController();
            const res = await getAttributeValues(id, q || null, { signal: attrValuesAbort.current.signal });
            const list: AttrValue[] = Array.isArray(res) ? res : (res?.data ?? []);
            setAttrValues(list);
        } catch (e: any) {
            if (e?.name !== "AbortError") console.error(e);
        }
    }, []);

    useEffect(() => {
        if (attrId && typeof attrId === "number") {
            loadAttrValues(attrId, attrValuesSearch);
        } else {
            setAttrValues([]);
        }
    }, [attrId, attrValuesSearch, loadAttrValues]);

    // debounce search for values
    const valuesTimer = useRef<number | undefined>(undefined);
    const onValuesSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setAttrValuesSearch(v);
        if (valuesTimer.current) window.clearTimeout(valuesTimer.current);
        valuesTimer.current = window.setTimeout(() => {
            if (attrId && typeof attrId === "number") loadAttrValues(attrId, v);
        }, 300) as unknown as number;
    };

    // manage selection of value ids
    const toggleValue = (id: number, on: boolean) => {
        setSelectedValueIds(prev => {
            if (on) {
                if (prev.includes(id)) return prev;
                return [...prev, id];
            }
            return prev.filter(x => x !== id);
        });
    };

    const removeSelectedValue = (id: number) => {
        setSelectedValueIds(prev => prev.filter(x => x !== id));
    };

    // ------ similar collections search
    const loadSimilar = useCallback(async (q: string) => {
        try {
            if (similarAbort.current) similarAbort.current.abort();
            similarAbort.current = new AbortController();
            const res = await getCollections(10, 1, q || null, { signal: similarAbort.current.signal });
            const list: MiniCollection[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            // Исключить уже выбранные
            const selIds = new Set(similarSelected.map(s => s.id));
            setSimilarResults(list.filter(i => !selIds.has(i.id)));
        } catch (e: any) {
            if (e?.name !== "AbortError") console.error(e);
        }
    }, [similarSelected]);

    const similarTimer = useRef<number | undefined>(undefined);
    const onSimilarSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setSimilarSearch(v);
        if (similarTimer.current) window.clearTimeout(similarTimer.current);
        similarTimer.current = window.setTimeout(() => loadSimilar(v), 300) as unknown as number;
    };

    const addSimilar = (c: MiniCollection) => {
        setSimilarSelected(prev => prev.some(x => x.id === c.id) ? prev : [...prev, c]);
        setSimilarResults(prev => prev.filter(x => x.id !== c.id));
    };
    const removeSimilar = (id: number) => {
        setSimilarSelected(prev => prev.filter(x => x.id !== id));
    };

    // ------ faq handlers
    const setFaqItem = (idx: number, key: keyof FaqItem, value: string) => {
        setFaq(list => {
            const next = [...list];
            next[idx] = { ...next[idx], [key]: value };
            return next;
        });
    };
    const addFaqItem = () => setFaq(list => [...list, { question: "", answer: "" }]);
    const removeFaqItem = (idx: number) =>
        setFaq(list => list.filter((_, i) => i !== idx));

    // ------ validation
    const valid = useMemo(() => {
        if (!name.trim()) return false;
        if (shortDescription.length > shortMax) return false;
        return true;
    }, [name, shortDescription]);

    // ------ submit
    const onSubmit = async () => {
        if (!valid) return;
        try {
            setSaving(true);
            const payload = {
                name: name.trim(),
                shortDescription: shortDescription.trim(),
                attributes: selectedValueIds,                     // array<number>
                similarCollections: similarSelected.map(s => s.id),
                faq: faq
                    .map(f => ({
                        question: (f.question || "").trim(),
                        answer: (f.answer || "").trim()
                    }))
                    .filter(f => f.question || f.answer),
            };
            const res = await createCollection(payload);
            // если API вернёт объект с id — уйдём на страницу просмотра/редактирования
            if (res?.id) {
                navigate(`/collections/${res.id}`);
            } else {
                navigate(`/collections`);
            }
        } catch (e) {
            console.error(e);
            alert("Failed to create collection. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="flex justify-between items-start gap-4">
                <div className="space-y-1">
                    <h1 className="text-xl font-semibold">Create collection</h1>
                    <div className="text-sm text-gray-600">Define filters, related collections, and FAQ.</div>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/collections" className="px-3 py-1 rounded border">Back</Link>
                    <button
                        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                        disabled={!valid || saving}
                        onClick={onSubmit}
                    >
                        {saving ? "Creating…" : "Create"}
                    </button>
                </div>
            </div>

            <div className="mt-3 p-3 rounded bg-white space-y-6">
                {/* Basic */}
                <section className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <label className="block text-sm mb-1">Name</label>
                            <input
                                className="border px-3 py-2 rounded w-full"
                                placeholder='e.g. "Mini-LED TVs"'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm mb-1">
                                Short description <span className="text-gray-500">(max {shortMax})</span>
                            </label>
                            <textarea
                                className="border px-3 py-2 rounded w-full min-h-[76px]"
                                placeholder="A concise 1–2 sentence summary shown on the collection page."
                                value={shortDescription}
                                onChange={(e) => setShortDescription(e.target.value)}
                                maxLength={shortMax}
                            />
                            <div className="text-xs text-gray-500 mt-1">{shortDescription.length}/{shortMax}</div>
                        </div>
                    </div>
                </section>

                {/* Filters */}
                <section className="space-y-3">
                    <h2 className="text-sm font-semibold">Filters (Attribute → Values)</h2>
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-[380px] w-full">
                            <div className="mb-2">
                                <label className="block text-sm mb-1">Attribute</label>
                                <select
                                    className="border px-2 py-2 rounded w-full"
                                    value={attrId}
                                    onChange={(e) => setAttrId(e.target.value ? Number(e.target.value) : "")}
                                >
                                    <option value="">— Select attribute —</option>
                                    {attributes.map(a => (
                                        <option key={a.id} value={a.id}>{a.name} (#{a.id})</option>
                                    ))}
                                </select>
                            </div>

                            {attrId ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm">Values</label>
                                        <input
                                            className="border px-2 py-1 rounded w-44"
                                            placeholder="Search…"
                                            defaultValue={attrValuesSearch}
                                            onChange={onValuesSearch}
                                        />
                                    </div>

                                    <div className="border rounded max-h-[280px] overflow-auto">
                                        {attrValues.length === 0 ? (
                                            <div className="p-3 text-sm text-gray-500">No values.</div>
                                        ) : (
                                            <div className="divide-y">
                                                {attrValues.map(v => {
                                                    const on = selectedValueIds.includes(v.id);
                                                    return (
                                                        <label key={v.id} className="flex items-center justify-between px-2 py-2">
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={on ? true : false}
                                                                    onChange={(e) => toggleValue(v.id, e.target.checked)}
                                                                />
                                                                <span className="text-sm">{v.value}</span>
                                                            </div>
                                                            <span className="text-[11px] text-gray-500">#{v.id}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex-1">
                            <label className="block text-sm mb-2">Selected values (applied to collection)</label>
                            {selectedValueIds.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 border rounded">No values selected yet.</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {selectedValueIds.map(id => (
                                        <span
                                            key={id}
                                            className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-100 text-sm"
                                        >
                                            #{id}
                                            <button
                                                className="text-gray-500 hover:text-gray-900"
                                                onClick={() => removeSelectedValue(id)}
                                                title="Remove"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Similar collections */}
                <section className="space-y-3">
                    <h2 className="text-sm font-semibold">Similar collections</h2>
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="md:w-[420px] w-full">
                            <input
                                className="border px-3 py-2 rounded w-full"
                                placeholder="Search collections…"
                                defaultValue={similarSearch}
                                onChange={onSimilarSearch}
                            />
                            <div className="mt-2 border rounded max-h-[240px] overflow-auto">
                                {similarResults.length === 0 ? (
                                    <div className="p-3 text-sm text-gray-500">No results.</div>
                                ) : (
                                    <div className="divide-y">
                                        {similarResults.map(c => (
                                            <div key={c.id} className="flex items-center justify-between px-2 py-2">
                                                <div className="text-sm">{c.name}</div>
                                                <button
                                                    className="px-2 py-1 rounded border text-sm"
                                                    onClick={() => addSimilar(c)}
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-1">
                            <label className="block text-sm mb-2">Selected</label>
                            {similarSelected.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500 border rounded">Empty.</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {similarSelected.map(c => (
                                        <span key={c.id} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 text-sm">
                                            {c.name} <span className="text-[11px] text-blue-700">#{c.id}</span>
                                            <button
                                                className="text-blue-700 hover:text-blue-900"
                                                onClick={() => removeSimilar(c.id)}
                                                title="Remove"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="space-y-3">
                    <h2 className="text-sm font-semibold">FAQ</h2>
                    <div className="space-y-3">
                        {faq.length === 0 ? (
                            <div className="text-sm text-gray-500">No FAQ yet.</div>
                        ) : (
                            faq.map((item, idx) => (
                                <div key={idx} className="border rounded p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs text-gray-500">Item #{idx + 1}</div>
                                        <button
                                            className="text-xs px-2 py-1 rounded border"
                                            onClick={() => removeFaqItem(idx)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    <input
                                        className="border px-3 py-2 rounded w-full"
                                        placeholder="Question"
                                        value={item.question}
                                        onChange={(e) => setFaqItem(idx, "question", e.target.value)}
                                    />
                                    <textarea
                                        className="border px-3 py-2 rounded w-full min-h-[80px]"
                                        placeholder="Answer"
                                        value={item.answer}
                                        onChange={(e) => setFaqItem(idx, "answer", e.target.value)}
                                    />
                                </div>
                            ))
                        )}
                        <button className="px-3 py-1 rounded border" onClick={addFaqItem}>
                            Add FAQ item
                        </button>
                    </div>
                </section>

                {/* Footer actions */}
                <section className="flex justify-end gap-2">
                    <Link to="/collections" className="px-3 py-1 rounded border">Cancel</Link>
                    <button
                        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                        disabled={!valid || saving}
                        onClick={onSubmit}
                    >
                        {saving ? "Creating…" : "Create collection"}
                    </button>
                </section>
            </div>
        </>
    );
}